import { useState, useCallback, useEffect } from "react";
import { liveQuery } from "dexie";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import db from "state/db";
import api from "state/axios";
import useOnlineStatus from "./useOnlineStatus";

const useSyncEngine = () => {
    const isOnline = useOnlineStatus();
    const offlineMode = useSelector((state) => state.auth?.offlineMode);
    const [syncing, setSyncing] = useState(false);
    const [pendingCount, setPendingCount] = useState(0);
    const [lastResult, setLastResult] = useState(null);

    // Reactively track pending count — updates immediately when Dexie changes
    useEffect(() => {
        const subscription = liveQuery(async () => {
            const orderCount = await db.orders.where("syncStatus").equals("pending").count();
            const sessionCount = await db.sessions
                .filter((s) => ["pending_create", "pending_close", "pending_reopen"].includes(s.syncStatus))
                .count();
            return orderCount + sessionCount;
        }).subscribe({
            next: (count) => setPendingCount(count),
            error: () => {},
        });
        return () => subscription.unsubscribe();
    }, []);

    const sync = useCallback(async (silent = false) => {
        if (!isOnline || syncing || offlineMode) return;
        setSyncing(true);

        let sessionsSynced = 0;
        let ordersSynced = 0;
        let failed = 0;

        try {
            // ── Step 1: sync pending_close sessions first ──
            // Must close old sessions before creating new ones (server enforces one active session)
            const pendingClose = await db.sessions
                .filter((s) => s.syncStatus === "pending_close")
                .toArray();

            for (const sess of pendingClose) {
                try {
                    await api.patch(`/inflight/sessions/${sess._id}/close`);
                    await db.sessions.update(sess._id, { syncStatus: "synced" });
                    sessionsSynced++;
                } catch (err) {
                    if (err.response?.status === 404) {
                        // Session doesn't exist on server (was never created) — just mark synced
                        await db.sessions.update(sess._id, { syncStatus: "synced" });
                        sessionsSynced++;
                    } else {
                        failed++;
                    }
                }
            }

            // ── Step 2: sync pending_create sessions ──
            const pendingCreate = await db.sessions
                .filter((s) => s.syncStatus === "pending_create")
                .toArray();

            for (const sess of pendingCreate) {
                try {
                    const { syncStatus, ...serverPayload } = sess;
                    await api.post("/inflight/sessions", serverPayload);
                    // If closed offline before sync, close on server too
                    if (sess.status === "closed") {
                        await api.patch(`/inflight/sessions/${sess._id}/close`);
                    }
                    await db.sessions.update(sess._id, { syncStatus: "synced" });
                    sessionsSynced++;
                } catch (err) {
                    // 409 means "you already have an active session" — a real conflict, not a duplicate
                    // Do NOT mark as synced; leave as pending so user can resolve
                    failed++;
                }
            }

            // ── Step 3: sync pending_reopen sessions ──
            const pendingReopen = await db.sessions
                .filter((s) => s.syncStatus === "pending_reopen")
                .toArray();

            for (const sess of pendingReopen) {
                try {
                    await api.patch(`/inflight/sessions/${sess._id}/reopen`);
                    await db.sessions.update(sess._id, { syncStatus: "synced" });
                    sessionsSynced++;
                } catch {
                    failed++;
                }
            }

            // ── Step 4: sync pending orders ──
            const pendingOrders = await db.orders.where("syncStatus").equals("pending").toArray();

            if (pendingOrders.length > 0) {
                const res = await api.post("/inflight/orders/batch", { orders: pendingOrders });

                if (res.data.success) {
                    const syncedIds = res.data.results
                        .filter((r) => r.status === "ok" || r.status === "duplicate")
                        .map((r) => r.clientId);
                    const failedIds = res.data.errors.map((e) => e.clientId);

                    if (syncedIds.length) {
                        await db.orders
                            .where("clientId").anyOf(syncedIds)
                            .modify({ syncStatus: "synced", syncedAt: new Date().toISOString() });
                        ordersSynced = syncedIds.length;
                    }
                    if (failedIds.length) {
                        await db.orders.where("clientId").anyOf(failedIds).modify({ syncStatus: "failed" });
                        failed += failedIds.length;
                    }
                }
            }

            const totalSynced = sessionsSynced + ordersSynced;
            if (totalSynced === 0 && failed === 0) {
                if (!silent) toast("Nothing pending to sync.");
            } else {
                const parts = [];
                if (sessionsSynced) parts.push(`${sessionsSynced} session(s)`);
                if (ordersSynced)   parts.push(`${ordersSynced} order(s)`);
                toast.success(`Synced ${parts.join(", ")}.`);

                await db.syncLog.add({
                    attemptedAt: new Date().toISOString(),
                    status: "success",
                    synced: totalSynced,
                    failed,
                });
            }

            setLastResult({ synced: totalSynced, failed });
        } catch (err) {
            await db.syncLog.add({
                attemptedAt: new Date().toISOString(),
                status: "error",
                error: err.message,
            });
            toast.error("Sync failed: " + (err?.response?.data?.message || err.message));
        } finally {
            setSyncing(false);
        }
    }, [isOnline, syncing, offlineMode]);

    // Auto-sync on reconnect — skip if offline mode (no JWT token available)
    useEffect(() => {
        if (isOnline && !offlineMode) {
            const timer = setTimeout(() => sync(true), 2000);
            return () => clearTimeout(timer);
        }
    }, [isOnline, offlineMode]); // eslint-disable-line react-hooks/exhaustive-deps

    return { sync, syncing, isOnline, pendingCount, lastResult };
};

export default useSyncEngine;
